'use client';

import { useStore } from '@tanstack/react-form';
import { TextButton } from '@tuic/web-compositions/core/TextButton';

import {
  ManifestCheckboxField,
  ManifestInputAreaField,
  ManifestInputField,
  ManifestMultiSelectField,
  ManifestPhoneField,
  ManifestRadioField,
  ManifestSelectField,
} from '../fields';
import { ManifestSection } from '../ManifestSection';
import { mapManifestOptionsToHtmlOptions } from '../utils';

import type { Manifest, ManifestFormValues } from '../types';
import type { SimpleFormApi } from '@lib/form/helper';
import type { AnyFieldApi } from '@tanstack/react-form';
import type { I18NInterface } from '@tuic/web-compositions/utils/i18n';
import type { FC } from 'react';

export type OtherInformationSectionProps = {
  form: SimpleFormApi<ManifestFormValues>;
  manifest?: Manifest;
  isDisabled?: boolean;

  i18n?: I18NInterface<
    | 'otherInfoTitle'
    | 'otherInfoDescription'
    | 'otherInfoNutritionTitle'
    | 'otherInfoDietType'
    | 'otherInfoDietNote'
    | 'otherInfoDietComments'
    | 'otherInfoAllergyType'
    | 'otherInfoAllergyNote'
    | 'otherInfoAllergyComments'
    | 'otherInfoSafetyTitle'
    | 'otherInfoSafetyDescription'
    | 'otherInfoDisability'
    | 'otherInfoDisabilityPlaceholder'
    | 'otherInfoEmergencyTitle'
    | 'otherInfoEmergencyDescription'
    | 'otherInfoEmergencyName'
    | 'otherInfoEmergencyPhone'
    | 'otherInfoEmergencyPhoneNote'
    | 'otherInfoHealthDataTitle'
    | 'otherInfoHealthDataDescription'
    | 'otherInfoHealthDataConfirm'
    | 'otherInfoChronicTitle'
    | 'otherInfoChronicConfirm'
    | 'otherInfoChronicDiabetes'
    | 'otherInfoChronicHypertension'
    | 'otherInfoChronicVascular'
    | 'otherInfoChronicStroke'
    | 'otherInfoChronicLung'
    | 'otherInfoChronicImmunodeficiency'
    | 'otherInfoChronicCortisone'
    | 'otherInfoChronicChemotherapy'
    | 'otherInfoChronicTumor'
    | 'otherInfoChronicImmunosuppression'
    | 'otherInfoChronicAntibody'
    | 'otherInfoChronicRadiation'
    | 'otherInfoHospitalInfo'
    | 'otherInfoPregnancyTitle'
    | 'otherInfoPregnancyConfirm'
    | 'otherInfoLanguageTitle'
    | 'otherInfoLanguageDescription'
    | 'otherInfoLanguageGerman'
    | 'otherInfoLanguageEnglish'
    | 'otherInfoSelectPlaceholder'
    | 'otherInfoMultiSelectConfirm'
    | 'otherInfoMultiSelectCancel'
    | 'otherInfoMultiSelectHint'
  >;
};

export const OtherInformationSection: FC<OtherInformationSectionProps> = ({
  form,
  i18n,
  manifest,
  isDisabled,
}) => {
  const dietaryOptions = manifest?.manifestOptions?.dietary;
  const otherOptions = manifest?.manifestOptions?.otherInformation;
  // TBD: Wert aus manifestOptions.personalInfo.salutationOptions verifizieren
  // und ggf. auf stabilen Code wechseln, sobald Backend/Options konsistent.
  const isFemale = manifest?.manifestData?.personalInfo?.salutation === 'Frau';

  // Gesundheitsdaten-Einwilligung wird nur angezeigt, wenn der Nutzer eines der
  // Ernährungs- oder Behinderungsfelder ausgefüllt hat. Sobald wieder alle leer
  // sind, verschwindet das Feld. Der reshape sendet in dem Fall `null`, selbst
  // wenn der Nutzer zwischendurch zugestimmt hatte — siehe ManifestFormBlock.
  const showHealthDataConsent = useStore(form.store, state => {
    const o = state.values.otherInformation;
    return (
      !!o.mealRequest ||
      !!o.individualRequest ||
      (o.allergy?.length ?? 0) > 0 ||
      !!o.allergyInfo ||
      !!o.handicap
    );
  });

  return (
    <ManifestSection pictogram="information" headline={i18n?.otherInfoTitle}>
      <p>{i18n?.otherInfoDescription}</p>

      {/* Ernaehrung */}
      <div className="space-y-4 mb-3">
        <h3 className="headline-md font-normal">
          {i18n?.otherInfoNutritionTitle}
        </h3>
        <div className="flex flex-col gap-4 md:flex-row md:gap-8">
          <form.Field name="otherInformation.mealRequest">
            {(field: AnyFieldApi) => (
              <ManifestSelectField
                field={field}
                label={i18n?.otherInfoDietType}
                note={i18n?.otherInfoDietNote}
                clearable
                clearLabel={i18n?.otherInfoSelectPlaceholder}
                options={mapManifestOptionsToHtmlOptions(
                  dietaryOptions?.mealRequestOptions,
                )}
                className="w-full"
                disabled={isDisabled}
              />
            )}
          </form.Field>
          <form.Field name="otherInformation.individualRequest">
            {(field: AnyFieldApi) => (
              <ManifestInputAreaField
                field={field}
                label={i18n?.otherInfoDietComments}
                className="w-full"
                disabled={isDisabled}
              />
            )}
          </form.Field>
        </div>
        <div className="flex flex-col gap-4 md:flex-row md:gap-8">
          <form.Field name="otherInformation.allergy">
            {(field: AnyFieldApi) => (
              <ManifestMultiSelectField
                name={field.name}
                value={(field.state.value as string[]) ?? []}
                onConfirm={next => field.handleChange(next)}
                label={i18n?.otherInfoAllergyType}
                placeholder={i18n?.otherInfoSelectPlaceholder}
                confirmLabel={i18n?.otherInfoMultiSelectConfirm}
                cancelLabel={i18n?.otherInfoMultiSelectCancel}
                hint={i18n?.otherInfoMultiSelectHint}
                options={dietaryOptions?.allergyOptions ?? []}
                className="w-full"
                disabled={isDisabled}
              />
            )}
          </form.Field>
          <form.Field name="otherInformation.allergyInfo">
            {(field: AnyFieldApi) => (
              <ManifestInputAreaField
                field={field}
                label={i18n?.otherInfoAllergyComments}
                className="w-full"
                disabled={isDisabled}
              />
            )}
          </form.Field>
        </div>
      </div>

      {/* Sicherheit */}
      <div className="space-y-4 my-3">
        <h3 className="headline-md font-normal">
          {i18n?.otherInfoSafetyTitle}
        </h3>
        <p>{i18n?.otherInfoSafetyDescription}</p>

        <div>
          <p className="label-md mb-1">{i18n?.otherInfoDisability}</p>
          <form.Field name="otherInformation.handicap">
            {(field: AnyFieldApi) => (
              <ManifestSelectField
                field={field}
                clearable
                clearLabel={i18n?.otherInfoDisabilityPlaceholder}
                options={mapManifestOptionsToHtmlOptions(
                  otherOptions?.handicapOptions,
                )}
                className="w-1/2"
                disabled={isDisabled}
              />
            )}
          </form.Field>
        </div>
      </div>

      {/* Kontakt fuer Notfaelle */}
      <div className="space-y-4 my-3">
        <h3 className="headline-md font-normal">
          {i18n?.otherInfoEmergencyTitle}
        </h3>
        <p>{i18n?.otherInfoEmergencyDescription}</p>
        <div className="flex flex-col gap-4 md:flex-row md:gap-8">
          <form.Field name="otherInformation.emergencyContactName">
            {(field: AnyFieldApi) => (
              <ManifestInputField
                field={field}
                required
                label={i18n?.otherInfoEmergencyName}
                className="w-full"
                disabled={isDisabled}
              />
            )}
          </form.Field>
          <form.Field name="otherInformation.emergencyContactPhone">
            {(field: AnyFieldApi) => (
              <ManifestPhoneField
                field={field}
                required
                label={i18n?.otherInfoEmergencyPhone}
                note={i18n?.otherInfoEmergencyPhoneNote}
                className="w-full"
                disabled={isDisabled}
              />
            )}
          </form.Field>
        </div>
      </div>

      {/* Gesundheitsdaten / Datenschutzeinwilligung */}
      {showHealthDataConsent && (
        <div className="space-y-4 my-3">
          <h3 className="headline-md font-normal">
            {i18n?.otherInfoHealthDataTitle}*
          </h3>
          <p>{i18n?.otherInfoHealthDataDescription}</p>
          <form.Field name="otherInformation.acceptDataProtectionTerms">
            {(field: AnyFieldApi) => (
              <ManifestCheckboxField
                field={field}
                required
                label={i18n?.otherInfoHealthDataConfirm}
                disabled={isDisabled}
              />
            )}
          </form.Field>
        </div>
      )}

      {/* Chronische Erkrankungen */}
      <div className="space-y-4 my-3">
        <h3 className="headline-md font-normal">
          {i18n?.otherInfoChronicTitle}*
        </h3>
        <form.Field name="otherInformation.acceptHealthTerms">
          {(field: AnyFieldApi) => (
            <ManifestCheckboxField
              field={field}
              required
              label={i18n?.otherInfoChronicConfirm}
              disabled={isDisabled}
            />
          )}
        </form.Field>
        <div className="space-y-2 pl-8">
          <div className="flex flex-col gap-4 md:flex-row md:gap-8">
            <ul className="list-disc space-y-1 pl-5">
              <li>{i18n?.otherInfoChronicDiabetes}</li>
              <li>{i18n?.otherInfoChronicHypertension}</li>
              <li>{i18n?.otherInfoChronicVascular}</li>
              <li>{i18n?.otherInfoChronicStroke}</li>
              <li>{i18n?.otherInfoChronicLung}</li>
              <li>{i18n?.otherInfoChronicImmunodeficiency}</li>
            </ul>
            <ul className="list-disc space-y-1 pl-5">
              <li>{i18n?.otherInfoChronicCortisone}</li>
              <li>{i18n?.otherInfoChronicChemotherapy}</li>
              <li>{i18n?.otherInfoChronicTumor}</li>
              <li>{i18n?.otherInfoChronicImmunosuppression}</li>
              <li>{i18n?.otherInfoChronicAntibody}</li>
              <li>{i18n?.otherInfoChronicRadiation}</li>
            </ul>
          </div>
          <TextButton>{i18n?.otherInfoHospitalInfo}</TextButton>
        </div>
      </div>

      {/* Schwangerschaft */}
      {isFemale && (
        <div className="space-y-4 my-3">
          <h3 className="headline-md font-normal">
            {i18n?.otherInfoPregnancyTitle}*
          </h3>
          <form.Field name="otherInformation.pregnant24Weeks">
            {(field: AnyFieldApi) => (
              <ManifestCheckboxField
                field={field}
                required
                label={i18n?.otherInfoPregnancyConfirm}
                disabled={isDisabled}
              />
            )}
          </form.Field>
        </div>
      )}

      {/* Bevorzugte Sprache an Bord */}
      <div className="space-y-4 mt-3">
        <h3 className="headline-md font-normal">
          {i18n?.otherInfoLanguageTitle}*
        </h3>
        <p>{i18n?.otherInfoLanguageDescription}</p>
        <form.Field name="otherInformation.preferredLanguageCode">
          {(field: AnyFieldApi) => (
            <ManifestRadioField
              field={field}
              options={[
                { value: 'DE', label: i18n?.otherInfoLanguageGerman ?? 'DE' },
                { value: 'EN', label: i18n?.otherInfoLanguageEnglish ?? 'EN' },
              ]}
              disabled={isDisabled}
            />
          )}
        </form.Field>
      </div>
    </ManifestSection>
  );
};
